import * as React from 'react'
import { toast } from 'sonner'

import type { AttachmentLite } from '@/gen/cortado/v1/attachments_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { formatBytes } from '@/lib/file-utils'
import {
  EMAIL_ATTACHMENT_LIMITS,
  useDeleteAttachment,
  useUploadEmailAttachment,
} from '@/stores/attachments.store'
import type { StagedAttachment } from './email-compose-state'

interface UseEmailAttachmentsOptions {
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
  onChange: () => void
}

export function useEmailAttachments({
  organizationId,
  parentType,
  parentId,
  onChange,
}: UseEmailAttachmentsOptions) {
  const [attachments, setAttachments] = React.useState<StagedAttachment[]>([])
  // Library attachments are persistent org assets. They must never be passed
  // to cleanupStagedAttachments, which deletes staged files server-side.
  const [libraryAttachments, setLibraryAttachments] = React.useState<
    AttachmentLite[]
  >([])
  const [libraryPickerOpen, setLibraryPickerOpen] = React.useState(false)
  const { upload: uploadAttachment } = useUploadEmailAttachment(
    organizationId,
    parentType,
    parentId,
  )
  const { mutation: deleteAttachmentMutation } = useDeleteAttachment()

  const uploadedBytes = React.useMemo(
    () =>
      attachments.reduce(
        (sum, attachment) => sum + Number(attachment.sizeBytes),
        0,
      ),
    [attachments],
  )
  const libraryBytes = React.useMemo(
    () =>
      libraryAttachments.reduce(
        (sum, attachment) => sum + Number(attachment.sizeBytes),
        0,
      ),
    [libraryAttachments],
  )
  const combinedCount = attachments.length + libraryAttachments.length
  const combinedBytes = uploadedBytes + libraryBytes
  const overCombinedFileLimit =
    combinedCount > EMAIL_ATTACHMENT_LIMITS.MAX_FILES
  const overCombinedSizeLimit =
    combinedBytes > EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES
  const overCombinedLimit = overCombinedFileLimit || overCombinedSizeLimit
  const combinedMeterPercent = Math.min(
    100,
    (combinedBytes / EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES) * 100,
  )
  const hasUploading = attachments.some((attachment) => attachment.uploading)

  const removeLibraryAttachment = React.useCallback((id: string) => {
    // Deselect only: library files are org assets, not compose-session files.
    setLibraryAttachments((previous) =>
      previous.filter((attachment) => attachment.id !== id),
    )
  }, [])

  const cleanupStagedAttachments = React.useCallback(
    (items: StagedAttachment[]) => {
      for (const attachment of items) {
        if (attachment.uploading || attachment.id.startsWith('temp-')) continue

        void deleteAttachmentMutation
          .mutateAsync({ id: attachment.id })
          .catch((error) => {
            reportError(error, {
              feature: 'email_attachment_cleanup',
              kind: 'mutation',
            })
            toast.error(`Failed to remove ${attachment.filename}`, {
              description: getErrorDescription(error),
            })
          })
      }
    },
    [deleteAttachmentMutation],
  )

  const handleFileDrop = React.useCallback(
    async (files: File[]) => {
      const filesToUpload: File[] = []
      let nextCount = combinedCount
      let nextTotal = combinedBytes

      for (const file of files) {
        if (nextCount >= EMAIL_ATTACHMENT_LIMITS.MAX_FILES) {
          toast.error(
            `Max ${EMAIL_ATTACHMENT_LIMITS.MAX_FILES} attachments per email`,
          )
          break
        }
        if (nextTotal + file.size > EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES) {
          toast.error(
            `Total attachment size cannot exceed ${formatBytes(EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES)}`,
          )
          break
        }
        filesToUpload.push(file)
        nextCount += 1
        nextTotal += file.size
      }

      for (const file of filesToUpload) {
        const tempId = `temp-${Date.now()}-${Math.random()}`
        setAttachments((previous) => [
          ...previous,
          {
            id: tempId,
            filename: file.name,
            sizeBytes: BigInt(file.size),
            uploading: true,
          },
        ])
        try {
          const uploaded = await uploadAttachment(file)
          setAttachments((previous) =>
            previous.map((attachment) =>
              attachment.id === tempId
                ? { ...uploaded, uploading: false }
                : attachment,
            ),
          )
          onChange()
        } catch (error) {
          setAttachments((previous) =>
            previous.filter((attachment) => attachment.id !== tempId),
          )
          toast.error(`Failed to upload ${file.name}`, {
            description: getErrorDescription(error),
          })
        }
      }
    },
    [combinedBytes, combinedCount, onChange, uploadAttachment],
  )

  const removeAttachment = React.useCallback(
    (id: string) => {
      const attachment = attachments.find((candidate) => candidate.id === id)
      setAttachments((previous) =>
        previous.filter((candidate) => candidate.id !== id),
      )
      if (attachment) {
        cleanupStagedAttachments([attachment])
        onChange()
      }
    },
    [attachments, cleanupStagedAttachments, onChange],
  )

  const resetAttachments = React.useCallback(() => {
    setAttachments([])
    setLibraryAttachments([])
  }, [])

  return {
    attachments,
    setAttachments,
    libraryAttachments,
    setLibraryAttachments,
    libraryPickerOpen,
    setLibraryPickerOpen,
    uploadedBytes,
    combinedCount,
    combinedBytes,
    combinedMeterPercent,
    overCombinedFileLimit,
    overCombinedLimit,
    hasUploading,
    handleFileDrop,
    removeAttachment,
    removeLibraryAttachment,
    cleanupStagedAttachments,
    resetAttachments,
  }
}
